const WALL = 1;
const FLOOR = 0;
const DOOR = 2;
const ENTRANCE = 9;

export type Point = { r: number; c: number };
export type Dungeon = number[][];

export interface DungeonResult {
  dungeon: Dungeon;
  enemies: Point[];
  treasures: Point[];
}

/**
 * Generates a navigable 1-tile width dungeon maze with rooms, loops, and objects.
 */
export function generateDungeon(
  rows: number,
  columns: number,
  numDoors: number = 0,
  numRooms: number = 0,
  numEnemies: number = 0,
  numTreasures: number = 0
): DungeonResult {
  // Enforce minimum size
  rows = Math.max(5, rows);
  columns = Math.max(5, columns);

  // Initialize with walls
  const dungeon: Dungeon = Array(rows)
    .fill(null)
    .map(() => Array(columns).fill(WALL));

  const visited: boolean[][] = Array(rows)
    .fill(null)
    .map(() => Array(columns).fill(false));

  // 1. Generate core 1-tile maze (Recursive Backtracking)
  carvePath(dungeon, visited, 1, 1, rows, columns);

  // 2. Fix the "Even Dimension" wall-lane issue
  ensureReachability(dungeon, rows, columns);

  // 3. Add Rooms (overlaps existing paths to ensure connectivity)
  if (numRooms > 0) {
    addRooms(dungeon, rows, columns, numRooms);
  }

  // 4. Add Loops (ensuring 1-tile width is maintained)
  createThinLoops(dungeon, rows, columns);

  // 5. Place Entrance and get its position
  const entrancePos = placeEntrance(dungeon, rows, columns);

  // 6. Place Doors (ensuring no adjacent doors)
  if (numDoors > 0) {
    placeDoors(dungeon, rows, columns, numDoors);
  }

  // 7. Refined Object Spawning
  const { enemies, treasures } = spawnObjectsRefined(
    dungeon,
    rows,
    columns,
    entrancePos,
    numEnemies,
    numTreasures
  );

  return { dungeon, enemies, treasures };
}

/**
 * Core maze carving logic
 */
function carvePath(dungeon: Dungeon, visited: boolean[][], row: number, col: number, rows: number, columns: number): void {
  visited[row][col] = true;
  dungeon[row][col] = FLOOR;

  const directions = [[-1, 0], [0, 1], [1, 0], [0, -1]];
  for (let i = directions.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [directions[i], directions[j]] = [directions[j], directions[i]];
  }

  for (const [dr, dc] of directions) {
    const nr = row + dr * 2, nc = col + dc * 2;
    if (nr >= 1 && nr < rows - 1 && nc >= 1 && nc < columns - 1 && !visited[nr][nc]) {
      dungeon[row + dr][col + dc] = FLOOR;
      carvePath(dungeon, visited, nr, nc, rows, columns);
    }
  }
}

/**
 * Creates rectangular rooms
 */
function addRooms(dungeon: Dungeon, rows: number, columns: number, numRooms: number): void {
  for (let i = 0; i < numRooms; i++) {
    const rH = Math.floor(Math.random() * 3) + 2;
    const rW = Math.floor(Math.random() * 3) + 2;
    const sR = Math.floor(Math.random() * (rows - rH - 2)) + 1;
    const sC = Math.floor(Math.random() * (columns - rW - 2)) + 1;
    for (let r = sR; r < sR + rH; r++) {
      for (let c = sC; c < sC + rW; c++) {
        dungeon[r][c] = FLOOR;
      }
    }
  }
}

/**
 * Door placement with adjacency checks
 */
function placeDoors(dungeon: Dungeon, rows: number, columns: number, numDoors: number): void {
  const potential: Point[] = [];
  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < columns - 1; c++) {
      if (dungeon[r][c] === FLOOR) {
        const horiz = dungeon[r - 1][c] === WALL && dungeon[r + 1][c] === WALL && dungeon[r][c + 1] === FLOOR && dungeon[r][c - 1] === FLOOR;
        const vert = dungeon[r][c + 1] === WALL && dungeon[r][c - 1] === WALL && dungeon[r - 1][c] === FLOOR && dungeon[r + 1][c] === FLOOR;
        if (horiz || vert) potential.push({ r, c });
      }
    }
  }

  potential.sort(() => Math.random() - 0.5);

  let placed = 0;
  for (const s of potential) {
    if (placed >= numDoors) break;
    const adjD = [[0, 1], [0, -1], [1, 0], [-1, 0]].some(([dr, dc]) => dungeon[s.r + dr]?.[s.c + dc] === DOOR);
    if (!adjD) {
      dungeon[s.r][s.c] = DOOR;
      placed++;
    }
  }
}

/**
 * Loop creation logic
 */
function createThinLoops(dungeon: Dungeon, rows: number, columns: number): void {
  const attempts = Math.floor((rows * columns) / 40);
  for (let i = 0; i < attempts; i++) {
    const r = Math.floor(Math.random() * (rows - 2)) + 1;
    const c = Math.floor(Math.random() * (columns - 2)) + 1;
    if (dungeon[r][c] === WALL) {
      const h = dungeon[r][c - 1] === FLOOR && dungeon[r][c + 1] === FLOOR;
      const v = dungeon[r - 1][c] === FLOOR && dungeon[r + 1][c] === FLOOR;
      if (h !== v && !createsRoom(dungeon, r, c)) {
        dungeon[r][c] = FLOOR;
      }
    }
  }
}

function createsRoom(dungeon: Dungeon, r: number, c: number): boolean {
  const corners = [
    [[-1, -1], [-1, 0], [0, -1]], [[-1, 1], [-1, 0], [0, 1]],
    [[1, -1], [1, 0], [0, -1]], [[1, 1], [1, 0], [0, 1]]
  ];
  return corners.some(g => g.every(([dr, dc]) => dungeon[r + dr][c + dc] === FLOOR));
}

/**
 * Fix for even dimensions
 */
function ensureReachability(dungeon: Dungeon, rows: number, columns: number): void {
  if (rows % 2 === 0) {
    const r = rows - 2, bc = 1 + (Math.floor(Math.random() * ((columns - 1) / 2)) * 2);
    if (bc < columns - 1) { dungeon[r][bc] = FLOOR; dungeon[r - 1][bc] = FLOOR; }
  }
  if (columns % 2 === 0) {
    const c = columns - 2, br = 1 + (Math.floor(Math.random() * ((rows - 1) / 2)) * 2);
    if (br < rows - 1) { dungeon[br][c] = FLOOR; dungeon[br][c - 1] = FLOOR; }
  }
}

/**
 * Entrance placement with inward tunneling fix
 */
function placeEntrance(dungeon: Dungeon, rows: number, columns: number): Point {
  const edge = Math.floor(Math.random() * 4);
  let er = 0, ec = 0;
  if (edge === 0) { er = 0; ec = Math.floor(Math.random() * (columns - 2)) + 1; }
  else if (edge === 1) { er = Math.floor(Math.random() * (rows - 2)) + 1; ec = columns - 1; }
  else if (edge === 2) { er = rows - 1; ec = Math.floor(Math.random() * (columns - 2)) + 1; }
  else { er = Math.floor(Math.random() * (rows - 2)) + 1; ec = 0; }

  dungeon[er][ec] = ENTRANCE;
  const dr = edge === 0 ? 1 : edge === 2 ? -1 : 0;
  const dc = edge === 3 ? 1 : edge === 1 ? -1 : 0;

  let currR = er + dr, currC = ec + dc;
  while (currR > 0 && currR < rows - 1 && currC > 0 && currC < columns - 1) {
    if (dungeon[currR][currC] === FLOOR) break;
    dungeon[currR][currC] = FLOOR;
    currR += dr; currC += dc;
  }
  return { r: er, c: ec };
}

/**
 * Refined Spawning logic (Entrance safety + Dead ends)
 */
function spawnObjectsRefined(
  dungeon: Dungeon,
  rows: number,
  columns: number,
  entrance: Point,
  numEnemies: number,
  numTreasures: number
): { enemies: Point[], treasures: Point[] } {
  const deadEnds: Point[] = [];
  const standardFloors: Point[] = [];

  const distMap = calculateDistances(dungeon, rows, columns, entrance);

  // Increase safe zone to ensure the immediate hallway is clear
  const minSafeDistance = 3;

  for (let r = 1; r < rows - 1; r++) {
    for (let c = 1; c < columns - 1; c++) {
      if (dungeon[r][c] === FLOOR) {
        // Strict check: No spawning on the entrance or within the immediate safe radius
        if ((distMap[r][c] || 0) < minSafeDistance) continue;

        const p = { r, c };
        const walls = [[0, 1], [0, -1], [1, 0], [-1, 0]]
          .filter(([dr, dc]) => dungeon[r + dr][c + dc] === WALL).length;

        if (walls === 3) {
          deadEnds.push(p);
        } else {
          standardFloors.push(p);
        }
      }
    }
  }

  // Shuffle both pools for maximum randomness
  const shuffle = (arr: any[]) => arr.sort(() => Math.random() - 0.5);
  shuffle(deadEnds);
  shuffle(standardFloors);

  // 1. Treasures: Prioritize dead ends, then use standard floors if needed
  const treasurePool = [...deadEnds, ...standardFloors];
  const treasures = treasurePool.slice(0, Math.min(numTreasures, treasurePool.length));

  // 2. Enemies: Pick from EVERYTHING remaining in the dungeon (dead ends AND standard floors)
  // This removes the bias toward dead ends for enemies.
  const remainingTiles = treasurePool.slice(treasures.length);
  shuffle(remainingTiles); // Re-shuffle to mix dead ends and floors thoroughly

  const enemies = remainingTiles.slice(0, Math.min(numEnemies, remainingTiles.length));

  return { enemies, treasures };
}

function calculateDistances(dungeon: Dungeon, rows: number, columns: number, start: Point): number[][] {
  const dists: number[][] = Array(rows).fill(null).map(() => Array(columns).fill(Infinity));
  const queue: Point[] = [start];
  dists[start.r][start.c] = 0;

  while (queue.length > 0) {
    const { r, c } = queue.shift()!;
    for (const [dr, dc] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < rows && nc >= 0 && nc < columns &&
        (dungeon[nr][nc] === FLOOR || dungeon[nr][nc] === DOOR) &&
        dists[nr][nc] === Infinity) {
        dists[nr][nc] = dists[r][c] + 1;
        queue.push({ r: nr, c: nc });
      }
    }
  }
  return dists;
}
